// Territory borders for the 3D view: a painted ribbon just inside every owned hex
// edge that faces different (or neutral) ground, coloured by the owning faction — so
// "whose land is whose" reads at a glance. One merged unlit mesh, rebuilt only when
// ownership changes. Pure view over GameState. Owner: client-render3d.

import { BufferAttribute, BufferGeometry, Color, DoubleSide, Mesh, MeshBasicMaterial, Scene } from 'three'
import { CHUNK, DIRS, chunkKey, hexToPixel, tileFromIdx } from '../../shared/hex'
import { HEX_SIZE, type GameState } from '../state'
import { CORNER, EDGE_DIR, ownerAt, tileHeight } from './terrainMesh'

const S = HEX_SIZE
const INSET = 0.86 // pull the ribbon toward the tile centre so borders sit inside
const WIDTH = 3.0 // ribbon half-width contribution (world units)
const LIFT = 0.7 // above the ground to beat z-fighting

export class Territory3d {
  private mesh: Mesh | null = null
  private sig = -1
  private mat = new MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.92, side: DoubleSide, depthWrite: false })

  constructor(private scene: Scene) {}

  sync(st: GameState): void {
    // Rebuild only when the loaded set / ownership (carried on chunk version) changes.
    let s = st.chunks.size
    for (const ck of st.chunks.values()) s = (Math.imul(s, 31) + ck.version) | 0
    if (s === this.sig) return
    this.sig = s
    if (this.mesh) { this.scene.remove(this.mesh); this.mesh.geometry.dispose() }
    this.mesh = this.build(st)
    if (this.mesh) this.scene.add(this.mesh)
  }

  private build(st: GameState): Mesh | null {
    const pos: number[] = []
    const col: number[] = []
    const c = new Color()
    for (const ck of st.chunks.values()) {
      for (let i = 0; i < CHUNK * CHUNK; i++) {
        const owner = ck.owners.get(i)
        if (!owner) continue
        const { q, r } = tileFromIdx(ck.cx, ck.cy, i)
        const p = hexToPixel(q, r, S)
        const y = tileHeight(st, q, r) + LIFT
        const hex = st.factions.get(owner)?.color ?? '#c8b48a'
        c.set(hex)
        for (let e = 0; e < 6; e++) {
          const d = DIRS[EDGE_DIR[e]]
          if (ownerAt(st, q + d.q, r + d.r) === owner) continue // interior edge
          // inset edge endpoints toward the tile centre
          const a0 = CORNER[e], a1 = CORNER[(e + 1) % 6]
          const ax = p.x + a0.x * INSET, az = p.y + a0.z * INSET
          const bx = p.x + a1.x * INSET, bz = p.y + a1.z * INSET
          // ribbon: offset the segment inward by its normal to give it width
          let nx = -(bz - az), nz = (bx - ax)
          const len = Math.hypot(nx, nz) || 1
          nx = (nx / len) * WIDTH; nz = (nz / len) * WIDTH
          // ensure the offset points toward the tile centre
          const mx = (ax + bx) / 2 - p.x, mz = (az + bz) / 2 - p.y
          if (nx * mx + nz * mz > 0) { nx = -nx; nz = -nz }
          // quad (ax,az)-(bx,bz)-(bx+n,bz+n)-(ax+n,az+n) as two tris
          const A = [ax, y, az], B = [bx, y, bz], C = [bx + nx, y, bz + nz], D = [ax + nx, y, az + nz]
          for (const t of [A, B, C, A, C, D]) { pos.push(t[0], t[1], t[2]); col.push(c.r, c.g, c.b) }
        }
      }
    }
    if (!pos.length) return null
    const g = new BufferGeometry()
    g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3))
    g.setAttribute('color', new BufferAttribute(new Float32Array(col), 3))
    return new Mesh(g, this.mat)
  }
}
